import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth0 } from '@auth0/auth0-react';
import { updateVenueRequestStatus } from '../utils/staffApiService';
import { normalizeRequestRows } from '../utils/requestAdapter';

const RequestsContext = createContext();

export const RequestsProvider = ({ children, venueId }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const { getAccessTokenSilently } = useAuth0();

  const applyRequests = useCallback((rows) => {
    const normalized = normalizeRequestRows(rows);
    setRequests(normalized);
    setNotificationCount(normalized.filter(request => request.status !== 'completed').length);
  }, []);

  const fetchRequests = useCallback(async () => {
    const { data, error } = await supabase
      .from('requests')
      .select(`
        *,
        table:tables (
          id,
          name
        )
      `)
      .order('created_at', { ascending: false })
      .eq('venue_id', venueId);

    if (error) {
      console.error('Error fetching requests:', error);
    } else {
      applyRequests(data);
    }
    setLoading(false);
  }, [applyRequests, venueId]);


  const updateRequestStatus = useCallback(async (id, newStatus) => {
    try {
      await updateVenueRequestStatus(venueId, id, newStatus, getAccessTokenSilently);
      await fetchRequests();
    } catch (error) {
      console.error('Error updating request status:', error.message);
      throw error;
    }
  }, [fetchRequests, getAccessTokenSilently, venueId]);

  useEffect(() => {
    fetchRequests();

    const requestsChannel = supabase
      .channel('public:requests')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'requests' }, payload => {
        if (payload.new.venue_id === venueId) {
          fetchRequests();
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'requests' }, payload => {
        if (payload.new.venue_id === venueId) {
          fetchRequests();
        }
      })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'requests' }, payload => {
        if (payload.old.venue_id === venueId) {
          fetchRequests();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(requestsChannel);
    };
  }, [fetchRequests, venueId]);

  return (
    <RequestsContext.Provider value={{ requests, loading, notificationCount, updateRequestStatus, refreshRequests: fetchRequests }}>
      {children}
    </RequestsContext.Provider>
  );
};

export const useRequestsContext = () => {
  return useContext(RequestsContext);
};

// Custom hook to omit notificationCount
export const useRequestsWithoutNotificationCount = () => {
  const { requests, loading, updateRequestStatus, refreshRequests } = useContext(RequestsContext);
  return { requests, loading, updateRequestStatus, refreshRequests };
};
